import { mapAmenitiesToFeature } from "config/amenityMap";

export function buildNmpFacilityNode(facility) {
	const schema = facility.schema || {};
	const loc = schema.location || {};
	const node = {
		"@type": "SelfStorage",
		name: schema.name,
		url: schema.url,
		address: {
			"@type": "PostalAddress",
			streetAddress: loc.streetAddress1,
			addressLocality: loc.city,
			addressRegion: loc.state,
			postalCode: loc.zip,
			addressCountry: "US"
		}
	};
	if (loc.latitude && loc.longitude) {
		node.geo = {
			"@type": "GeoCoordinates",
			latitude: loc.latitude,
			longitude: loc.longitude
		};
	}
	if (schema.facilityPhone) {
		node.telephone = schema.facilityPhone;
	}
	if (schema.reviews?.average && schema.reviews?.number) {
		node.aggregateRating = {
			"@type": "AggregateRating",
			ratingValue: schema.reviews.average,
			ratingCount: schema.reviews.number
		};
	}
	node.priceRange = "$ - $$";
	if (schema.paymentAccepted?.length) {
		node.paymentAccepted = schema.paymentAccepted;
	}
	node.currenciesAccepted = "USD";
	if (schema.promotion) {
		const priceMatches = typeof schema.promotion === "string"
			? schema.promotion.match(/\$[\d,]+(?:\.\d{2})?/g)
			: null;
		const price = priceMatches?.length
			? priceMatches[0].replace("$", "").replace(/,/g, "")
			: null;
		node.makesOffer = {
			"@type": "Offer",
			name: schema.promotion,
			description: schema.promotion,
			...(price && { price, priceCurrency: "USD" }),
			availability: "https://schema.org/InStock"
		};
	}
	const amenityFeature = mapAmenitiesToFeature({
		amenities: schema.amenities || {}
	});
	if (amenityFeature?.length) {
		node.amenityFeature = amenityFeature;
	}
	if (schema.openingHoursSpecification?.length) {
		node.openingHoursSpecification = schema.openingHoursSpecification;
	}
	if (schema.images?.length) {
		node.image = schema.images.map((img) => ({
			"@type": "ImageObject",
			url: img
		}));
	}
	if (loc.latitude && loc.longitude) {
		node.hasMap = `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
	}
	return node;
}

export function buildNmpSchema({ facilityCards, baseUrl, meta, headline }) {
	const facilityNodes = facilityCards.map(buildNmpFacilityNode);
	return {
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "WebSite",
				name: "Storage.com",
				url: `${baseUrl}/`,
				potentialAction: {
					"@type": "SearchAction",
					target: {
						"@type": "EntryPoint",
						urlTemplate: `${baseUrl}/search/?city=`,
						inLanguage: "en-US",
						actionPlatform: [
							"http://schema.org/DesktopWebPlatform",
							"http://schema.org/MobileWebPlatform"
						]
					},
					"query-input": {
						"@type": "PropertyValueSpecification",
						valueRequired: "http://schema.org/True",
						valueName: "search_term_string"
					}
				}
			},
			{
				"@type": "BreadcrumbList",
				itemListElement: [
					{
						"@type": "ListItem",
						position: 1,
						item: {
							"@id": `${baseUrl}/`,
							name: "Home",
							url: `${baseUrl}/`
						}
					},
					{
						"@type": "ListItem",
						position: 2,
						item: {
							"@id": meta?.canonical,
							name: headline || 'Storage Units Near Me',
							url: meta?.canonical
						}
					}
				]
			},
			{
				"@type": "WebPage",
				name: headline || 'Storage Units Near Me',
				url: meta?.canonical,
				description: meta?.description,
				// ItemList here declares the WebPage's primary content type per SEO spec.
				// Individual SelfStorage nodes in @graph serve as the list items.
				mainEntity: {
					"@type": "ItemList",
					name: headline || 'Storage Units Near Me',
					description: `List of storage facilities offering ${headline || 'Storage Units Near Me'} near the user's location`,
				}
			},
			...facilityNodes
		]
	};
}
